import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { LoadingButton } from '@mui/lab';

import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';
import {
  type TravelArrangementsData,
  type TravelArrangementsFormProps
} from '../types';
import {
  FixedAmountSchema,
  NoCompensationSchema,
  VariableBasedOnAddressSchema,
  VariableBasedOnDistanceInServiceSchema,
  VariableBasedOnDistanceSchema,
  VariableBasedOnPostalCodeSchema
} from '../schema';
import DateSection from './DateSection';
import ArrangementAndCompensationSection from './ArrangementAndCompensationSection';
import LocationSection from './LocationSection';
import DistanceAndAmountSection from './DistanceSection';
import CommentsSection from './CommentsSection';
import { type TravelCompensationType } from 'components/admin-portal/Tables/TravelCosts/Arrangements/types';
import { arrangementVisibilityConfig } from '../utils';
import { TRAVEL_ARRANGEMENTS } from 'RestAPIs/endpoints';
import { useSaveResource } from 'RestAPIs/useSaveResource';
import { useEffect, useMemo, useState } from 'react';
import useGetEmployeeDetails from '../hooks/useGetEmployeeDetails';

const arrangementSchemaMap = {
  NoCompensationSchema,
  VariableBasedOnAddressSchema,
  VariableBasedOnPostalCodeSchema,
  VariableBasedOnDistanceSchema,
  VariableBasedOnDistanceInServiceSchema,
  FixedAmountSchema
};

const TravelArrangementsForm = ({
  rowData,
  employeeId,
  handleClose,
  isCopy
}: TravelArrangementsFormProps) => {
  const { t } = useTranslation('common');
  const [selectedArrangement, setSelectedArrangement] = useState<
    TravelCompensationType | undefined
  >();
  const toDate = rowData?.valid?.to;
  const isNewRow = rowData && Object.values(rowData).length === 0;
  const { data: employeeDetails } = useGetEmployeeDetails(employeeId);
  const postalCode = employeeDetails?.adrResPostalcode;

  const matchedConfigItem = useMemo(() => {
    return arrangementVisibilityConfig.find(
      (item) => t(item.translationKey) === selectedArrangement?.name
    );
  }, [selectedArrangement, t]);

  const dynamicSchema = useMemo(() => {
    if (matchedConfigItem && matchedConfigItem.schema) {
      const schemaFn = arrangementSchemaMap[matchedConfigItem.schema];
      return schemaFn(t);
    }
    return NoCompensationSchema();
  }, [matchedConfigItem, t]);

  const currentResolver = useMemo(
    () => yupResolver(dynamicSchema),
    [dynamicSchema]
  );

  const methods = useForm<TravelArrangementsData>({
    defaultValues: rowData,
    resolver: currentResolver
  });

  const { control, handleSubmit, watch, setValue, reset } = methods;
  const selectedArrangementType = watch('arrangementTypes') as
    | TravelCompensationType
    | undefined;

  useEffect(() => {
    selectedArrangementType && setSelectedArrangement(selectedArrangementType);
  }, [selectedArrangementType]);

  useEffect(() => {
    reset(
      (currValues) => ({
        ...currValues,
        ...(isNewRow && {
          postalCode
        })
      }),
      {
        keepDefaultValues: true
      }
    );
  }, [dynamicSchema, reset, isNewRow]);

  const { mutateAsync: saveForm, isLoading } = useSaveResource({
    url: TRAVEL_ARRANGEMENTS,
    params: { employeeId },
    id: rowData?.empTrvArrangementId,
    invalidateQueryKey: ['travelArrangements', 'list', employeeId],
    isCopy
  });

  const onSubmit = async (data: TravelArrangementsData) => {
    const {
      arrangementTypes,
      countryCode,
      trvSet,
      valid,
      distanceKm,
      travelExpensesNominal,
      waNr,
      ...rest
    } = data;

    const payload = {
      ...rest,
      compensationTypeName: arrangementTypes?.name,
      compensationTypeId: arrangementTypes?.id,
      countryCode: countryCode?.value ?? null,
      trvSetDescription: trvSet?.description || null,
      trvSetId: trvSet?.trvSetId || null,
      valid: {
        from: valid?.from ?? null,
        to: valid?.to ?? null
      },
      distanceKm: distanceKm ? Number(distanceKm) : null,
      travelExpensesNominal: Number(travelExpensesNominal) || null,
      waId: waNr ?? null
    };

    await saveForm(payload);
    handleClose();
  };

  // Look up the current scenario from the config
  const currentScenario = arrangementVisibilityConfig.find(
    (item) => t(item.translationKey) === selectedArrangementType?.name
  );

  // Helper to check if a field or entire section should be hidden
  const isHidden = (fieldIdentifier: string) =>
    currentScenario?.hiddenFields.includes(fieldIdentifier);

  return (
    <MultiStepForm methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <DateSection control={control} setValue={setValue} toDate={toDate} />
      <ArrangementAndCompensationSection
        rowData={rowData ?? {}}
        control={control}
        setValue={setValue}
        employeeId={employeeId}
        selectedArrangementType={selectedArrangementType}
      />

      {selectedArrangementType && !isHidden('LocationSection') && (
        <LocationSection
          control={control}
          rowData={rowData ?? {}}
          setValue={setValue}
          isFieldHidden={isHidden}
          watch={watch}
        />
      )}

      {selectedArrangementType && !isHidden('DistanceAndAmountSection') && (
        <DistanceAndAmountSection
          control={control}
          watch={watch}
          setValue={setValue}
          isFieldHidden={isHidden}
        />
      )}

      <CommentsSection control={control} />

      <MultiStepForm.Actions>
        <LoadingButton
          loading={isLoading}
          type="submit"
          variant="contained"
          autoFocus
          color="primary"
        >
          {t('save')}
        </LoadingButton>
      </MultiStepForm.Actions>
    </MultiStepForm>
  );
};

export default TravelArrangementsForm;
